import React, { useRef, useState, useEffect } from 'react'
import { Box, FormHelperText, InputLabel, Theme, IconButton, Tooltip, Popover, TextField, Button } from '@mui/material'
import { makeStyles } from '@mui/styles'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import InsertLinkIcon from '@mui/icons-material/InsertLink'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
  },
  toolbar: {
    display: 'flex',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  editor: {
    padding: theme.spacing(1),
    minHeight: '100px',
    outline: 'none',
    '&:focus': {
      outline: 'none',
    },
  },
  editorWrapper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  iconButton: {
    padding: theme.spacing(0.5),
    margin: theme.spacing(0, 0.5),
  },
  active: {
    backgroundColor: theme.palette.action.selected,
  },
  popoverContent: {
    padding: theme.spacing(2),
    width: '300px',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
  },
  labelRoot: {
    transform: 'translate(0, -1.5px) scale(0.75)',
    transformOrigin: 'top left',
    color: theme.palette.text.secondary,
    fontWeight: 400,
    marginBottom: theme.spacing(0.5),
  },
  helperTextRight: {
    textAlign: 'right',
  },
}))

interface SimpleRichTextEditorProps {
  id: string;
  label?: string;
  value?: string;
  onChange: (html: string) => void;
  maxLength?: number;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  helperText?: string;
}

const SimpleRichTextEditor: React.FC<SimpleRichTextEditorProps> = ({
  id,
  label,
  value = '',
  onChange,
  maxLength = 500,
  disabled = false,
  className,
  error = false,
  helperText,
}) => {
  const classes = useStyles()
  const editorRef = useRef<HTMLDivElement>(null)
  const [charCount, setCharCount] = useState(0)
  const [linkPopoverAnchor, setLinkPopoverAnchor] = useState<HTMLButtonElement | null>(null)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkText, setLinkText] = useState('')

  // Store the current selection range to restore it after link insertion
  const savedSelectionRef = useRef<Range | null>(null)

  // Initialize editor with value
  useEffect(() => {
    if (editorRef.current && value) {
      editorRef.current.innerHTML = value
      updateCharCount()
    }
  }, [])

  // Update character count - count the FULL HTML content including tags
  const updateCharCount = () => {
    if (editorRef.current) {
      const htmlContent = editorRef.current.innerHTML
      setCharCount(htmlContent.length)
    }
  }

  // Save the last valid HTML content within limits
  const [lastValidHtml, setLastValidHtml] = useState(value)

  // Save the current selection
  const saveSelection = () => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange()
    }
  }

  // Restore the saved selection
  const restoreSelection = () => {
    if (savedSelectionRef.current && editorRef.current) {
      const selection = window.getSelection()
      if (selection) {
        selection.removeAllRanges()
        selection.addRange(savedSelectionRef.current)
      }
    }
  }

  // Handle content changes
  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML

      // Update the character count based on full HTML
      updateCharCount()

      // If within the character limit, save the content and update
      if (html.length <= maxLength) {
        setLastValidHtml(html)
        onChange(html)
      } else {
        // If exceeding the limit, revert to the last valid HTML
        const selection = window.getSelection()
        const hadFocus = document.activeElement === editorRef.current

        // Remember cursor position approximately
        const cursorPosition = selection?.anchorOffset || 0

        // Restore last valid content
        editorRef.current.innerHTML = lastValidHtml
        updateCharCount()

        // Try to restore focus and an approximate cursor position if possible
        if (hadFocus) {
          editorRef.current.focus()

          try {
            // Try to set cursor at the end
            const range = document.createRange()
            if (editorRef.current.childNodes.length > 0) {
              const lastChild = editorRef.current.childNodes[editorRef.current.childNodes.length - 1]
              const offset = lastChild.textContent?.length || 0
              range.setStart(lastChild, Math.min(offset, cursorPosition))
              range.collapse(true)

              selection?.removeAllRanges()
              selection?.addRange(range)
            }
          } catch (e) {
            // Selection restoration failed, not critical
          }
        }
      }
    }
  }

  // Format actions
  const execCommand = (command: string, value: string = '') => {
    if (disabled) return

    // Restore the selection before executing command if we're in the link insertion workflow
    if (command === 'createLink') {
      restoreSelection()
    }

    document.execCommand(command, false, value)
    if (editorRef.current) {
      editorRef.current.focus()
      onChange(editorRef.current.innerHTML)
    }
  }

  // Handle bold
  const handleBold = () => {
    execCommand('bold')
  }

  // Handle italic
  const handleItalic = () => {
    execCommand('italic')
  }

  // Handle underline
  const handleUnderline = () => {
    execCommand('underline')
  }

  // Open link popover
  const handleLinkClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return

    // Save the current selection before opening the popover
    saveSelection()

    // Get selected text
    const selection = window.getSelection()
    const selectedText = selection?.toString() || ''
    setLinkText(selectedText)

    setLinkPopoverAnchor(event.currentTarget)
  }

  // Close link popover
  const handleLinkClose = () => {
    setLinkPopoverAnchor(null)
    setLinkUrl('')
    setLinkText('')
  }

  // Insert link
  const handleLinkInsert = () => {
    if (!linkUrl) {
      handleLinkClose()
      return
    }

    // If there's selected text, create a link with it using the saved selection
    if (savedSelectionRef.current) {
      // Restore the selection to where it was when the link button was clicked
      restoreSelection()

      const selection = window.getSelection()
      if (selection && !selection.isCollapsed) {
        // Use the standard command to create a link from the selection
        execCommand('createLink', linkUrl)
      } else if (linkText) {
        // If there's no selection but we have link text, insert at the cursor position
        const linkElement = document.createElement('a')
        linkElement.href = linkUrl
        linkElement.textContent = linkText
        linkElement.target = '_blank'

        // Insert HTML at the current selection point
        document.execCommand('insertHTML', false, linkElement.outerHTML)
      }
    } else if (editorRef.current) {
      // Fallback - if we somehow lost the selection, insert at the end
      editorRef.current.focus()
      const linkElement = document.createElement('a')
      linkElement.href = linkUrl
      linkElement.textContent = linkText
      linkElement.target = '_blank'

      document.execCommand('insertHTML', false, linkElement.outerHTML)
    }

    // Update content and close popover
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML)
    }

    handleLinkClose()
  }

  // Track if we should block the next input
  const [blockNextInput, setBlockNextInput] = useState(false)

  // Handle before input to strictly enforce character limit
  const handleBeforeInput = (e: React.FormEvent<HTMLDivElement>) => {
    if (editorRef.current) {
      // Pre-check if we're at the limit
      const html = editorRef.current.innerHTML
      if (html.length >= maxLength) {
        // Using e.preventDefault() doesn't always work with contentEditable
        // So we'll set a flag and handle it in handleInput
        setBlockNextInput(true)
        return
      }
      setBlockNextInput(false)
    }
  }

  // Pass keydown events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent adding more content when at limit
    if (editorRef.current && editorRef.current.innerHTML.length >= maxLength &&
      !e.ctrlKey && !e.metaKey &&
      e.key !== 'Backspace' && e.key !== 'Delete' &&
      e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' &&
      e.key !== 'ArrowUp' && e.key !== 'ArrowDown' &&
      e.key !== 'Home' && e.key !== 'End' &&
      e.key !== 'Tab') {
      e.preventDefault()
    }
  }

  return (
    <Box className={`${classes.root} ${className}`}>
      {label && (
        <InputLabel className={classes.labelRoot} htmlFor={id} error={error}>
          {label}
        </InputLabel>
      )}
      <Box
        className={classes.editorWrapper}
        sx={{
          borderColor: error ? 'error.main' : 'divider',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <div className={classes.toolbar}>
          <Tooltip title="Fett">
            <IconButton
              className={classes.iconButton}
              onClick={handleBold}
              disabled={disabled}
              size="small"
            >
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic">
            <IconButton
              className={classes.iconButton}
              onClick={handleItalic}
              disabled={disabled}
              size="small"
            >
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Unterstrichen">
            <IconButton
              className={classes.iconButton}
              onClick={handleUnderline}
              disabled={disabled}
              size="small"
            >
              <FormatUnderlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="URL einfügen">
            <IconButton
              className={classes.iconButton}
              onClick={handleLinkClick}
              disabled={disabled}
              size="small"
            >
              <InsertLinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <div
          ref={editorRef}
          id={id}
          className={classes.editor}
          contentEditable={!disabled}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onBeforeInput={handleBeforeInput}
          onPaste={(e) => {
            // For paste operations, we need to handle character limits
            if (editorRef.current) {
              const currentLength = editorRef.current.innerHTML.length
              const clipboardData = e.clipboardData
              const pastedText = clipboardData.getData('text/html') || clipboardData.getData('text')

              // If pasting would exceed the limit, prevent default paste
              if (currentLength + pastedText.length > maxLength) {
                e.preventDefault()

                // Optionally paste a truncated version if there's room
                if (currentLength < maxLength) {
                  const availableSpace = maxLength - currentLength
                  const truncatedText = pastedText.substring(0, availableSpace)
                  document.execCommand('insertHTML', false, truncatedText)
                  updateCharCount()
                  onChange(editorRef.current.innerHTML)
                }
              }
            }
          }}
          onBlur={() => {
            if (editorRef.current) {
              onChange(editorRef.current.innerHTML)
            }
          }}
        />
      </Box>
      <FormHelperText error={error} className={classes.helperTextRight}>
        {helperText || `${charCount}/${maxLength}`}
      </FormHelperText>

      {/* Link Popover */}
      <Popover
        open={Boolean(linkPopoverAnchor)}
        anchorEl={linkPopoverAnchor}
        onClose={handleLinkClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box className={classes.popoverContent}>
          <TextField
            label="URL einfügen"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            fullWidth
            margin="dense"
            variant="outlined"
            placeholder="https://"
          />
          <TextField
            label="URL Text"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            fullWidth
            margin="dense"
            variant="outlined"
            placeholder="Link text"
          />
          <Box className={classes.buttonContainer}>
            <Button onClick={handleLinkClose} sx={{ mr: 1 }}>
              Abbrechen
            </Button>
            <Button
              onClick={handleLinkInsert}
              variant="contained"
              color="primary"
              disabled={!linkUrl}
            >
              Einfügen
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}

export default SimpleRichTextEditor
